import React, { useState } from 'react';
import { 
  FileText, User, GraduationCap, Upload, CreditCard, 
  CheckCircle2, ArrowRight, ArrowLeft, ShieldCheck, 
  Sparkles, Download, Printer, AlertCircle, Globe, Award, 
  Briefcase, BookOpen, Layers, Clock, Search
} from 'lucide-react';
import { DEPARTMENTS_AND_PROGRAMMES, INSTITUTION_INFO } from '../../data/portalData';
import { NIGERIAN_STATES_AND_LGAS, INTERNATIONAL_COUNTRIES } from '../../data/nigerianStatesAndLgas';
import { PaymentCardIcons } from './PaymentCardIcons';
import { PaystackModal } from './PaystackModal';

export const ApplicationForm = ({ setActiveView, onApplicationSubmitted }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPaystackModal, setShowPaystackModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    // Name Breakdown
    title: 'Dr (Engr)',
    surname: 'Umoh',
    firstName: 'Uduakobong',
    middleName: 'Peter',
    nameOnCertificate: 'UMOH, Uduakobong Peter',
    
    // Contact & Bio
    email: 'u.umoh@petroenergy.ng',
    phone: '+234 803 762 9910',
    gender: 'Male',
    
    // Nationality & Domicile
    applicantType: 'Nigerian', // 'Nigerian' | 'International'
    stateOfOrigin: 'Akwa Ibom State',
    lga: 'Uyo',
    countryOfCitizenship: 'Nigeria',
    internationalProvince: '',
    passportOrIdNumber: '',

    // Professional Dossier
    currentEmployer: 'Niger Delta Petroleum Exploration Ltd',
    jobTitle: 'Senior Vice President (Operations)',
    yearsExperience: '12',
    
    // Programme Selection
    department: 'Business Administration',
    programme: 'Doctor of Business Administration (DBA)',
    studyMode: 'Modular Executive Weekends',
    
    // Academic History
    undergraduateInstitution: 'University of Uyo',
    undergraduateDegree: 'B.Eng Mechanical Engineering',
    undergraduateClass: 'Second Class Upper (2:1)',
    undergraduateYear: '2012',
    postgraduateInstitution: 'University of Lagos',
    postgraduateDegree: 'M.Sc Mechanical Engineering',
    postgraduateYear: '2016',
    
    // Dynamic Programme-Specific Fields
    // 1. PhD Fields
    phdResearchSynopsis: 'Digital Transformation, Artificial Intelligence, and Strategic Resilience in Downstream Petroleum Logistics: An Akwa Ibom Corridor Framework',
    phdMethodology: 'Quantitative Econometric Modeling (PLS-SEM)',
    phdMastersCgpa: '4.35 / 5.00',
    phdPublications: '2 Peer-Reviewed Articles (Journal of African Business & Development Economics)',

    // 2. DBA / DPA / Executive Doctorate Fields
    dbaProblemStatement: 'Operational resilience and downtime mitigation across deep offshore support logistics under volatile foreign exchange conditions',
    dbaExecutiveTenure: '8 Years at Senior Executive / C-Suite Rank',
    dbaProfessionalBodies: 'FNSE, COREN, NIM (Chartered), CIBN (Affiliate)',
    dbaSponsorshipMode: 'Corporate Sponsored (Employer Supported)',

    // 3. Master's (MBA / EMPA / EMAF) Fields
    nyscCertificateNo: 'NYSC/AK/2013/084920',
    undergraduateDegreeClass: 'Second Class Upper (2:1)',
    supervisorySpan: '34 Direct & Indirect Reports across Regional Operations',
    masterCareerGoal: 'Executive Transition to Board Level Corporate Governance',

    // 4. PGD Fields
    pgdQualifyingHndDegree: 'HND Mechanical Engineering (Distinction) / B.Sc Third Class Equivalency',
    pgdBridgingRationale: 'Academic qualification elevation and bridging towards Executive Master of Business Administration matriculation',
    pgdPostGradExperience: '7 Years Post-Qualification Industrial Practice',

    // Documents
    degreeCertUploaded: true,
    transcriptUploaded: true,
    cvUploaded: true,
    statementUploaded: true,
    researchTopic: 'Digital Transformation and Strategic Resilience in Deep Offshore Asset Maintenance'
  });

  // Handle standard input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Dedicated Name change handler that keeps Certificate Name in sync
  const handleNameChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      const s = field === 'surname' ? value : updated.surname;
      const f = field === 'firstName' ? value : updated.firstName;
      const m = field === 'middleName' ? value : updated.middleName;
      
      const sPart = (s || '').trim().toUpperCase();
      const fPart = (f || '').trim();
      const mPart = (m || '').trim();
      
      // Auto-format format: SURNAME, Firstname Middlename
      const autoCertName = sPart ? `${sPart}, ${fPart}${mPart ? ' ' + mPart : ''}`.trim() : `${fPart}${mPart ? ' ' + mPart : ''}`.trim();
      
      return {
        ...updated,
        nameOnCertificate: autoCertName
      };
    });
  };

  // State change handler to reset LGA to valid choices for the state
  const handleStateChange = (e) => {
    const newState = e.target.value;
    const lgasForState = NIGERIAN_STATES_AND_LGAS[newState] || [];
    setFormData(prev => ({
      ...prev,
      stateOfOrigin: newState,
      lga: lgasForState[0] || ''
    }));
  };

  // Helpers to detect selected programme category
  const isPhdProgramme = formData.programme.toLowerCase().includes('phd') || 
                          formData.programme.toLowerCase().includes('philosophy');
  
  const isExecutiveDoctorate = (formData.programme.toLowerCase().includes('dba') || 
                                formData.programme.toLowerCase().includes('dpa') || 
                                formData.programme.toLowerCase().includes('doctor of business') ||
                                formData.programme.toLowerCase().includes('doctor of public') ||
                                formData.programme.toLowerCase().includes('doctor of accounting')) && !isPhdProgramme;

  const isMastersProgramme = formData.programme.toLowerCase().includes('master') || 
                             formData.programme.toLowerCase().includes('mba') || 
                             formData.programme.toLowerCase().includes('empa') || 
                             formData.programme.toLowerCase().includes('emaf');

  const isPgdProgramme = formData.programme.toLowerCase().includes('postgraduate diploma') || 
                         formData.programme.toLowerCase().includes('pgd');

  const handleOpenPaystack = () => {
    setShowPaystackModal(true);
  };

  const candidateDisplayName = `${formData.title ? formData.title + ' ' : ''}${formData.surname} ${formData.firstName} ${formData.middleName}`.trim();

  const handlePaymentSuccess = (paymentDetails) => {
    setShowPaystackModal(false);
    setPaymentCompleted(true);
    
    const generatedAppNo = "AKSU-BS-2026-APP-" + Math.floor(1000 + Math.random() * 9000);
    const newAppRecord = {
      ...formData,
      fullName: `${formData.surname}, ${formData.firstName} ${formData.middleName}`.trim(),
      applicationNo: generatedAppNo,
      programmeApplied: formData.programme,
      category: isPhdProgramme || isExecutiveDoctorate ? "Doctoral" : isMastersProgramme ? "Master's" : "Postgraduate Diploma",
      qualifications: `${formData.highestQualification} (${formData.institutionAttended})`,
      workExperience: `${formData.yearsOfExperience} Years (${formData.jobTitle} at ${formData.currentEmployer})`,
      applicationFeePaid: true,
      paystackRef: paymentDetails.reference,
      paystackDate: paymentDetails.paidAt || new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      screeningScore: null,
      screeningStatus: "Pending", // Strictly Pending Screening & Credential Verification
      decisionDate: "Awaiting Committee Review",
      admissionLetterIssued: false,
      matricNo: null, // Matriculation number is strictly NOT assigned upon application
      documents: [
        { name: "Degree / Qualifying Certificate", verified: true },
        { name: "Official Academic Transcript", verified: false },
        { name: "Curriculum Vitae / Professional Profile", verified: true },
        { name: "Statement of Intent / Concept Proposal", verified: true }
      ]
    };

    setSubmittedApplication(newAppRecord);
    if (onApplicationSubmitted) {
      onApplicationSubmitted(newAppRecord);
    }
    setCurrentStep(4); // Acknowledgement step
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6">
      
      {/* Top Banner in AKSU Emblem Crimson and Forest Green */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 mb-8 shadow-xl relative overflow-hidden border-2 border-[#7A470E]">
        <div className="absolute right-0 top-0 w-64 h-64 bg-[#7A470E]/15 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-wrap items-center justify-between gap-4 relative z-10">
          <div>
            <span className="text-xs uppercase font-extrabold tracking-widest text-[#B87A28] font-sc">
              Admissions Portal · 2025/2026 & 2026/2027 Session
            </span>
            <h2 className="text-2xl sm:text-4xl font-black font-sc mt-1 text-white">
              Online Application for Executive Admission Consideration
            </h2>
            <p className="text-xs sm:text-sm text-slate-200 mt-1 max-w-xl leading-relaxed">
              Complete your personal, academic, and programme dossier. Pay the non-refundable ₦30,000 registration fee via Paystack. <span className="text-amber-300 font-semibold block mt-0.5">Note: Submission of an application and payment of fee initiates the screening and credential audit process and does not constitute an offer or guarantee of admission.</span>
            </p>
          </div>
          <div className="text-right">
            <span className="text-xs text-[#B87A28] font-sc font-bold block">Registration Fee:</span>
            <span className="text-3xl font-black text-white font-sc">₦30,000</span>
          </div>
        </div>

        {/* Stepper Wizard Indicator in AKSU Brand Colors */}
        <div className="mt-8 pt-6 border-t border-slate-800 grid grid-cols-4 gap-2 text-xs">
          <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-[#B87A28] font-black' : 'text-slate-400'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${currentStep >= 1 ? 'bg-[#7A470E] text-white' : 'bg-[#030F1C] text-slate-400'}`}>1</span>
            <span className="hidden sm:inline font-sc">Bio-data</span>
          </div>
          <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-[#B87A28] font-black' : 'text-slate-400'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${currentStep >= 2 ? 'bg-[#0B2644] text-white' : 'bg-[#030F1C] text-slate-400'}`}>2</span>
            <span className="hidden sm:inline font-sc">Programme</span>
          </div>
          <div className={`flex items-center space-x-2 ${currentStep >= 3 ? 'text-[#B87A28] font-black' : 'text-slate-400'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${currentStep >= 3 ? 'bg-[#7A470E] text-white' : 'bg-[#030F1C] text-slate-400'}`}>3</span>
            <span className="hidden sm:inline font-sc">Paystack Fee</span>
          </div>
          <div className={`flex items-center space-x-2 ${currentStep >= 4 ? 'text-[#B87A28] font-black' : 'text-slate-400'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${currentStep >= 4 ? 'bg-[#0B2644] text-white' : 'bg-[#030F1C] text-slate-400'}`}>4</span>
            <span className="hidden sm:inline font-sc">Acknowledgement</span>
          </div>
        </div>
      </div>

      {/* Main Multi-Step Form Container */}
      <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-md p-6 sm:p-8">
        
        {/* STEP 1: Personal Profile & Biographical Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="border-b-2 border-slate-100 pb-3">
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Step 1: Personal Dossier & Identity
              </h3>
              <p className="text-xs text-slate-500">
                Provide accurate names and official details exactly as they appear on your qualifying certificates.
              </p>
            </div>

            {/* Title & 4 Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Title</label>
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                >
                  <option>Mr</option>
                  <option>Mrs</option>
                  <option>Ms</option>
                  <option>Dr</option>
                  <option>Dr (Engr)</option>
                  <option>Prof</option>
                  <option>Engr</option>
                  <option>Chief</option>
                  <option>Obong</option>
                  <option>Barrister</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                  Surname (Last Name) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="surname"
                  value={formData.surname}
                  onChange={(e) => handleNameChange('surname', e.target.value)}
                  placeholder="e.g. Umoh"
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleNameChange('firstName', e.target.value)}
                  placeholder="e.g. Uduakobong"
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={(e) => handleNameChange('middleName', e.target.value)}
                  placeholder="e.g. Peter"
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                />
              </div>
            </div>

            {/* Name as would appear on Certificate */}
            <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-200">
              <label className="text-xs font-bold text-slate-900 block mb-1 font-sc flex items-center justify-between">
                <span>Name as would appear on Certificate <span className="text-red-500">*</span></span>
                <span className="text-[10px] text-amber-800 font-semibold bg-amber-100 px-2 py-0.5 rounded">
                  Official Parchment & Gazette
                </span>
              </label>
              <input
                type="text"
                name="nameOnCertificate"
                value={formData.nameOnCertificate}
                onChange={handleChange}
                placeholder="SURNAME, Firstname Middlename"
                className="w-full text-xs sm:text-sm p-2.5 bg-white border border-amber-300 rounded-lg text-slate-950 font-bold tracking-wide focus:outline-none focus:border-[#7A470E]"
                required
              />
              <p className="text-[11px] text-slate-600 mt-1.5">
                This exact sequence will be embossed on your Degree Parchment, Official Academic Transcript, and University Senate Gazette.
              </p>
            </div>

            {/* Contact Information (Clean Email Field with no browser/password manager icons) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                  Official Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="email"
                    name="email"
                    id="applicant_email_clean"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-protonpass-ignore="true"
                    data-bitwarden-ignore="true"
                    placeholder="e.g. name@organisation.com"
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>
                <span className="text-[10px] text-slate-400 mt-0.5 block">
                  Official notifications and Paystack receipt will be delivered to this address.
                </span>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                  Telephone (WhatsApp) <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+234 800 000 0000"
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                />
              </div>
            </div>

            {/* Applicant Type Selection: Nigerian vs International */}
            <div className="pt-2 border-t border-slate-100">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                  Nationality & Domicile
                </span>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100 text-xs">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, applicantType: 'Nigerian' }))}
                    className={`px-3 py-1 font-bold rounded-md transition-all ${
                      formData.applicantType === 'Nigerian'
                        ? 'bg-white text-[#0B2644] shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Nigerian Citizen
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, applicantType: 'International' }))}
                    className={`px-3 py-1 font-bold rounded-md transition-all flex items-center space-x-1 ${
                      formData.applicantType === 'International'
                        ? 'bg-[#0B2644] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Globe className="w-3 h-3 mr-1" />
                    <span>International Applicant</span>
                  </button>
                </div>
              </div>

              {formData.applicantType === 'Nigerian' ? (
                /* Nigerian 36 States + FCT and All LGAs */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      State of Origin (All 36 States + FCT) <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="stateOfOrigin"
                      value={formData.stateOfOrigin}
                      onChange={handleStateChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    >
                      {Object.keys(NIGERIAN_STATES_AND_LGAS).map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      Local Government Area (LGA) <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="lga"
                      value={formData.lga}
                      onChange={handleChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    >
                      {(NIGERIAN_STATES_AND_LGAS[formData.stateOfOrigin] || []).map(lgaName => (
                        <option key={lgaName} value={lgaName}>{lgaName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                /* International Applicant Options */
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      Country of Citizenship / Residence <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="countryOfCitizenship"
                      value={formData.countryOfCitizenship}
                      onChange={handleChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    >
                      {INTERNATIONAL_COUNTRIES.map(ctry => (
                        <option key={ctry} value={ctry}>{ctry}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      State / Province / Region <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="internationalProvince"
                      value={formData.internationalProvince}
                      onChange={handleChange}
                      placeholder="e.g. Greater Accra, Texas, Ontario"
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      Passport Number / National ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="passportOrIdNumber"
                      value={formData.passportOrIdNumber}
                      onChange={handleChange}
                      placeholder="e.g. A09283741"
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Employment and Corporate Background */}
            <div className="pt-2 border-t border-slate-100">
              <h4 className="text-xs font-black uppercase tracking-wider text-[#7A470E] mb-3 font-sc">
                Current Employment & Professional Standing
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Employer / MDA / Organisation</label>
                  <input
                    type="text"
                    name="currentEmployer"
                    value={formData.currentEmployer}
                    onChange={handleChange}
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Designation / Executive Rank</label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Post-Graduation Experience</label>
                  <select
                    name="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={handleChange}
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  >
                    <option value="3">3 - 5 Years</option>
                    <option value="7">6 - 9 Years</option>
                    <option value="12">10 - 15 Years</option>
                    <option value="20">15+ Years</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="px-6 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl text-sm transition-all flex items-center space-x-2 shadow-sm cursor-pointer font-sc"
              >
                <span>Continue to Step 2: Programme Selection</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Programme Selection & Dynamic Programme-Based Dossier */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="border-b-2 border-slate-100 pb-3">
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Step 2: Programme Selection & Academic Record
              </h3>
              <p className="text-xs text-slate-500">
                Choose your degree programme. Specific screening questions will dynamically adapt to your selected qualification.
              </p>
            </div>

            {/* Department and Programme Choice */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Academic Department</label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                >
                  <option>Business Administration</option>
                  <option>Public Administration</option>
                  <option>Accounting and Finance</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Degree Programme Choice</label>
                <select
                  name="programme"
                  value={formData.programme}
                  onChange={handleChange}
                  className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-bold focus:outline-none focus:border-[#7A470E]"
                >
                  <option>Doctor of Philosophy (PhD) in Business Administration</option>
                  <option>Doctor of Business Administration (DBA)</option>
                  <option>Master of Business Administration (MBA)</option>
                  <option>Postgraduate Diploma in Business Administration (PGD)</option>
                  <option>Executive Master of Public Administration (EMPA)</option>
                  <option>Doctor of Public Administration (DPA)</option>
                  <option>Executive Master of Accounting and Finance (EMAF)</option>
                  <option>Doctor of Accounting and Finance</option>
                  <option>Diploma in Business Administration</option>
                  <option>Professional Certificate in Business Administration</option>
                </select>
              </div>
            </div>

            {/* Standard Academic Qualifications */}
            <div className="space-y-4 pt-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                Undergraduate Academic Background
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Undergraduate Degree & Institution</label>
                  <input
                    type="text"
                    name="undergraduateDegree"
                    value={formData.undergraduateDegree}
                    onChange={handleChange}
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Year of Graduation</label>
                  <input
                    type="text"
                    name="undergraduateYear"
                    value={formData.undergraduateYear}
                    onChange={handleChange}
                    className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>
              </div>

              {(isPhdProgramme || isExecutiveDoctorate) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">
                      Postgraduate Degree & Awarding University (M.Sc / MBA / M.Phil)
                    </label>
                    <input
                      type="text"
                      name="postgraduateDegree"
                      value={formData.postgraduateDegree}
                      onChange={handleChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 font-sc">Year Obtained</label>
                    <input
                      type="text"
                      name="postgraduateYear"
                      value={formData.postgraduateYear}
                      onChange={handleChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* DYNAMIC PROGRAMME-SPECIFIC DOSSIER QUESTIONS */}
            <div className="pt-2">
              {/* CASE 1: PhD in Business Administration */}
              {isPhdProgramme && (
                <div className="p-5 rounded-xl bg-amber-50/40 border-2 border-amber-300 space-y-4">
                  <div className="flex items-center space-x-2 border-b border-amber-200 pb-2">
                    <BookOpen className="w-4 h-4 text-[#7A470E]" />
                    <h5 className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                      Doctor of Philosophy (PhD) Screening Requirements
                    </h5>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Master's Degree Weighted Average / CGPA (Senate Min: 3.50 / 5.0) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="phdMastersCgpa"
                        value={formData.phdMastersCgpa}
                        onChange={handleChange}
                        placeholder="e.g. 4.25 / 5.00 (60% or B average)"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Methodological Orientation / Framework <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="phdMethodology"
                        value={formData.phdMethodology}
                        onChange={handleChange}
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                      >
                        <option>Quantitative Econometric Modeling (PLS-SEM / Stata)</option>
                        <option>Mixed-Method Explanatory Sequential (Saunders Onion)</option>
                        <option>Qualitative Inquiry & Grounded Theory (NVivo)</option>
                        <option>Action Research & Critical Realism</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                      Intended Doctoral Thesis Topic / Theoretical Grounding <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      name="phdResearchSynopsis"
                      value={formData.phdResearchSynopsis}
                      onChange={handleChange}
                      placeholder="Outline theoretical anchors (e.g. Resource-Based View, Institutional Theory) and practitioner gap"
                      className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                      Prior Scholarly Publications / Academic Papers
                    </label>
                    <input
                      type="text"
                      name="phdPublications"
                      value={formData.phdPublications}
                      onChange={handleChange}
                      placeholder="Journals, conference proceedings, or research monographs"
                      className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                </div>
              )}

              {/* CASE 2: Executive Doctorates (DBA / DPA / Dr of Accounting) */}
              {isExecutiveDoctorate && (
                <div className="p-5 rounded-xl bg-blue-50/40 border-2 border-blue-200 space-y-4">
                  <div className="flex items-center space-x-2 border-b border-blue-200 pb-2">
                    <Award className="w-4 h-4 text-[#0B2644]" />
                    <h5 className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                      Executive Doctorate (DBA/DPA) Professional Dossier
                    </h5>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Senior Executive / C-Suite Experience Tenure <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="dbaExecutiveTenure"
                        value={formData.dbaExecutiveTenure}
                        onChange={handleChange}
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#0B2644]"
                      >
                        <option>5 - 7 Years at Senior Management Level</option>
                        <option>8 - 12 Years as Senior VP / Director / C-Suite Officer</option>
                        <option>12+ Years as Managing Director / CEO / Permanent Secretary</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Professional Memberships / Fellowships (ICAN, NIM, CIBN, IoD)
                      </label>
                      <input
                        type="text"
                        name="dbaProfessionalBodies"
                        value={formData.dbaProfessionalBodies}
                        onChange={handleChange}
                        placeholder="e.g. FNSE, COREN, NIM (Chartered), CIBN (Affiliate)"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#0B2644]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                      Boardroom / Industry Problem Statement for Executive Dissertation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      name="dbaProblemStatement"
                      value={formData.dbaProblemStatement}
                      onChange={handleChange}
                      placeholder="Specify the operational, strategic, or governance challenge your executive doctoral project will resolve"
                      className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#0B2644]"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                      Corporate / Institutional Sponsorship Structure
                    </label>
                    <select
                      name="dbaSponsorshipMode"
                      value={formData.dbaSponsorshipMode}
                      onChange={handleChange}
                      className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#0B2644]"
                    >
                      <option>Corporate Sponsored (Employer Supported)</option>
                      <option>Self-Sponsored Executive</option>
                      <option>MDA / Government Agency Sponsored</option>
                      <option>Multilateral / Bilateral Development Partner</option>
                    </select>
                  </div>
                </div>
              )}

              {/* CASE 3: Master's Programmes (MBA / EMPA / EMAF) */}
              {isMastersProgramme && (
                <div className="p-5 rounded-xl bg-emerald-50/40 border-2 border-emerald-200 space-y-4">
                  <div className="flex items-center space-x-2 border-b border-emerald-200 pb-2">
                    <Briefcase className="w-4 h-4 text-emerald-800" />
                    <h5 className="text-xs font-black uppercase tracking-wider text-emerald-900 font-sc">
                      Executive Master's (MBA/EMPA/EMAF) Dossier
                    </h5>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        NYSC Discharge / Exemption Certificate No <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nyscCertificateNo"
                        value={formData.nyscCertificateNo}
                        onChange={handleChange}
                        placeholder="e.g. NYSC/AK/2013/084920"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-700"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Undergraduate Degree Classification <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="undergraduateClass"
                        value={formData.undergraduateClass}
                        onChange={handleChange}
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-700"
                      >
                        <option>First Class Honours</option>
                        <option>Second Class Honours (Upper Division - 2:1)</option>
                        <option>Second Class Honours (Lower Division - 2:2)</option>
                        <option>HND with Distinction / Upper Credit + PGD</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Current Supervisory Span (Direct/Indirect Reports)
                      </label>
                      <input
                        type="text"
                        name="supervisorySpan"
                        value={formData.supervisorySpan}
                        onChange={handleChange}
                        placeholder="e.g. 15 Direct Reports / Unit Lead"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-700"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Career Transition / Progression Objective
                      </label>
                      <input
                        type="text"
                        name="masterCareerGoal"
                        value={formData.masterCareerGoal}
                        onChange={handleChange}
                        placeholder="e.g. Boardroom appointment, C-Suite elevation, Corporate Turnaround"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-700"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* CASE 4: Postgraduate Diploma (PGD) */}
              {isPgdProgramme && (
                <div className="p-5 rounded-xl bg-purple-50/40 border-2 border-purple-200 space-y-4">
                  <div className="flex items-center space-x-2 border-b border-purple-200 pb-2">
                    <Layers className="w-4 h-4 text-purple-800" />
                    <h5 className="text-xs font-black uppercase tracking-wider text-purple-900 font-sc">
                      Postgraduate Diploma (PGD) Bridging Requirements
                    </h5>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                      Qualifying HND / Third-Class Degree Details <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="pgdQualifyingHndDegree"
                      value={formData.pgdQualifyingHndDegree}
                      onChange={handleChange}
                      placeholder="e.g. HND Mechanical Engineering (Distinction) / B.Sc Third Class Equivalency"
                      className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-purple-700"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Post-Graduation Industrial Experience <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="pgdPostGradExperience"
                        value={formData.pgdPostGradExperience}
                        onChange={handleChange}
                        placeholder="e.g. 5 Years Post-NYSC / Industrial Practice"
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-purple-700"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1 font-sc">
                        Target Pathway Following PGD Completion
                      </label>
                      <select
                        name="pgdBridgingRationale"
                        value={formData.pgdBridgingRationale}
                        onChange={handleChange}
                        className="w-full text-xs sm:text-sm p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-purple-700"
                      >
                        <option>Executive Master of Business Administration (MBA)</option>
                        <option>Executive Master of Public Administration (EMPA)</option>
                        <option>Executive Master of Accounting & Finance (EMAF)</option>
                        <option>Professional Career Acceleration</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Verified Document Upload Badges */}
            <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200">
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block mb-2 font-sc">
                Required Screening Documents Attached:
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="flex items-center space-x-1.5 text-[#7A470E] bg-white px-2 py-1 rounded border border-[#7A470E]/40 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Degree Cert (PDF)</span>
                </div>
                <div className="flex items-center space-x-1.5 text-[#7A470E] bg-white px-2 py-1 rounded border border-[#7A470E]/40 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Official Transcript</span>
                </div>
                <div className="flex items-center space-x-1.5 text-[#7A470E] bg-white px-2 py-1 rounded border border-[#7A470E]/40 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Curriculum Vitae</span>
                </div>
                <div className="flex items-center space-x-1.5 text-[#7A470E] bg-white px-2 py-1 rounded border border-[#7A470E]/40 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Research Intent</span>
                </div>
              </div>
            </div>

            {/* Stepper Navigation */}
            <div className="pt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="px-4 py-2.5 text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center space-x-1 font-sc"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Bio-data</span>
              </button>

              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="px-6 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl text-sm transition-all flex items-center space-x-2 shadow-sm cursor-pointer font-sc"
              >
                <span>Proceed to Step 3: Paystack Fee Payment</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Review & Paystack Application Fee Settlement */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="border-b-2 border-slate-100 pb-3">
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Step 3: Application Review & Paystack Payment
              </h3>
              <p className="text-xs text-slate-500">
                Verify your complete submission summary and process the statutory ₦25,000 application fee via Paystack.
              </p>
            </div>

            {/* Dossier Summary Card (Cleaned of any Executive prefix on student name) */}
            <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-200 text-xs space-y-3">
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Candidate Name:</span>
                <strong className="text-slate-900 font-bold">{candidateDisplayName}</strong>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Name as on Certificate:</span>
                <strong className="text-[#0B2644] font-mono font-bold">{formData.nameOnCertificate}</strong>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Target Programme:</span>
                <strong className="text-[#0B2644] font-bold font-sc">{formData.programme}</strong>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Department:</span>
                <strong className="text-slate-900 font-bold">{formData.department}</strong>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Nationality & Location:</span>
                <span className="text-slate-900">
                  {formData.applicantType === 'Nigerian' 
                    ? `${formData.stateOfOrigin} (${formData.lga} LGA), Nigeria` 
                    : `${formData.countryOfCitizenship} (${formData.internationalProvince || 'International'})`}
                </span>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Corporate Designation:</span>
                <span className="text-slate-900">{formData.jobTitle} at {formData.currentEmployer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Registration Fee Payable:</span>
                <span className="font-mono font-black text-lg text-[#0B2644]">₦30,000.00</span>
              </div>
            </div>

            {/* Statutory Application Fee Notice */}
            <div className="p-4 rounded-2xl bg-amber-50 border-2 border-amber-300/80 text-xs text-amber-950 flex items-start space-x-3 shadow-2xs">
              <AlertCircle className="w-5 h-5 text-[#7A470E] shrink-0 mt-0.5" />
              <div className="space-y-1">
                <strong className="font-sc font-bold text-[#7A470E] block text-[11px] uppercase tracking-wider">
                  Statutory University Admissions Notice
                </strong>
                <p className="text-xs text-slate-800 leading-relaxed font-medium">
                  The ₦30,000 fee is a non-refundable registration and administrative processing fee payable at the point of online registration. 
                  Payment of this fee and submission of the application dossier does <strong>NOT</strong> constitute 
                  an offer or guarantee of admission. All applications are subject to competitive merit ranking, 
                  transcript audit, and Postgraduate Admissions Board / Senate approval.
                </p>
              </div>
            </div>

            {/* Paystack Integration Box with Payment Card Badges */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-[#030F1C] to-[#0B2644] text-white space-y-4 border-2 border-[#7A470E]">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00C3F7] text-slate-950 flex items-center justify-center font-black text-xl">
                    P
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white font-sc">Pay with Paystack</h4>
                    <p className="text-xs text-slate-300">Instant credit/debit card, bank transfer & USSD gateway</p>
                  </div>
                </div>
                <span className="text-[11px] bg-[#0B2644] text-white font-bold px-2 py-0.5 rounded border border-[#7A470E]/40">
                  Secure Checkout
                </span>
              </div>

              {/* Payment Card Badges */}
              <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-700/60">
                <span className="text-[10px] uppercase font-bold text-slate-300 block mb-2 tracking-wider">
                  Supported Payment Cards & Channels:
                </span>
                <PaymentCardIcons />
              </div>

              <p className="text-xs text-slate-200 leading-relaxed">
                Clicking the button below launches the official Paystack dialogue. Your reference and transaction 
                clearance will instantly attach to your admissions docket.
              </p>

              <button
                type="button"
                onClick={handleOpenPaystack}
                className="w-full py-3.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black rounded-xl text-sm transition-all shadow-lg flex items-center justify-center space-x-2 cursor-pointer font-sc"
              >
                <CreditCard className="w-4 h-4" />
                <span>Launch Paystack to Pay ₦30,000 Registration Fee</span>
              </button>
            </div>

            <div className="pt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2.5 text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center space-x-1 font-sc"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Programme</span>
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Successful Application Acknowledgement & Screening Guidance */}
        {currentStep === 4 && submittedApplication && (
          <div className="space-y-6 py-4">
            
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto shadow-inner border border-emerald-300">
                <CheckCircle2 className="w-9 h-9" />
              </div>

              <div>
                <span className="text-[11px] font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-full border border-[#7A470E]/40 font-sc">
                  Application Dossier Logged · Registration Fee Settled (₦30,000)
                </span>
                <h3 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644] mt-2">
                  Application Acknowledged: {submittedApplication.nameOnCertificate || submittedApplication.fullName}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-xl mx-auto leading-relaxed">
                  Your online registration dossier and Paystack clearance receipt have been successfully registered into the Akwa Ibom State University Admissions Queue.
                </p>
              </div>
            </div>

            {/* MANDATORY SUBMISSION CALLOUT (Official Prospectus Step 5) */}
            <div className="max-w-2xl mx-auto p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-500 text-left shadow-md">
              <div className="flex items-start space-x-3">
                <Printer className="w-6 h-6 text-emerald-800 shrink-0 mt-0.5" />
                <div className="space-y-1.5">
                  <h4 className="text-xs font-black uppercase tracking-wider text-emerald-950 font-sc flex items-center space-x-2">
                    <span>Mandatory Official Step 5: Physical Submission to Liaison Office</span>
                    <span className="bg-emerald-200 text-emerald-900 text-[9px] px-1.5 py-0.2 rounded font-mono font-bold">REQUIRED</span>
                  </h4>
                  <p className="text-xs text-emerald-900 leading-relaxed font-semibold">
                    Print a copy of this completed registration form and submit it, along with your credentials (certificates, transcripts, CV, and ID), to:
                  </p>
                  <div className="p-3 bg-white rounded-xl border border-emerald-300 font-mono text-xs text-slate-900 leading-relaxed">
                    <strong>The Director, AKSU Business School</strong><br />
                    AKSU Liaison Office, 6 Tony Asikpo Street, Shelter Afrique,<br />
                    P.M.B 1167, Uyo, Akwa Ibom State.
                  </div>
                </div>
              </div>
            </div>

            {/* Statutory University Governance Disclaimer (Crucial Callout) */}
            <div className="max-w-2xl mx-auto p-4 sm:p-5 rounded-2xl bg-amber-50 border-2 border-amber-300/90 text-left shadow-sm">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-[#7A470E] shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                    Statutory Academic Governance Notice
                  </h4>
                  <p className="text-xs text-slate-800 leading-relaxed font-medium">
                    <strong>Submission of an application does not guarantee admission.</strong> All candidate dossiers are subject to rigorous academic transcript verification, institutional credential audit, competitive merit screening by the Departmental Committee, and final approval by the Postgraduate Admissions Board and University Senate.
                  </p>
                </div>
              </div>
            </div>

            {/* Official Application Dossier Summary Card */}
            <div className="max-w-2xl mx-auto bg-slate-50 rounded-2xl p-5 sm:p-6 border-2 border-slate-200 text-xs space-y-3 shadow-sm text-left">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">Application Docket Number:</span>
                <span className="font-mono font-bold text-[#0B2644] text-sm bg-white px-2.5 py-0.5 rounded border border-slate-200">
                  {submittedApplication.applicationNo}
                </span>
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">Paystack Transaction Reference:</span>
                <span className="font-mono text-slate-800 text-xs bg-white px-2 py-0.5 rounded border border-slate-200">
                  {submittedApplication.paystackRef}
                </span>
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">Programme Applied For:</span>
                <strong className="text-slate-900 font-bold text-xs">{submittedApplication.programme}</strong>
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">Candidate Legal Name:</span>
                <span className="font-bold text-slate-900">{submittedApplication.nameOnCertificate || submittedApplication.fullName}</span>
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">Screening & Evaluation Status:</span>
                <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
                  <Clock className="w-3.5 h-3.5 text-amber-700 animate-pulse" />
                  <span>Pending Screening & Credential Verification</span>
                </span>
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <span className="text-slate-500 font-semibold font-sc">University Matriculation Number:</span>
                <span className="text-[11px] font-semibold text-slate-500 italic bg-slate-100 px-2 py-0.5 rounded">
                  Pending Board Decision & Acceptance Fee Payment
                </span>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-slate-500 font-semibold font-sc">Target Intake Commencement:</span>
                <span className="font-mono font-bold text-[#7A470E]">October 2026</span>
              </div>
            </div>

            {/* Next Steps Guidance */}
            <div className="max-w-2xl mx-auto bg-blue-50/70 border border-blue-200 rounded-2xl p-4 text-left text-xs text-blue-950 space-y-1.5">
              <span className="font-bold font-sc uppercase tracking-wider text-blue-900 text-[11px] block">
                Next Stages in Your Admissions Journey:
              </span>
              <ul className="space-y-1 list-disc pl-4 text-slate-700">
                <li>Your academic transcripts and uploaded certificates will be evaluated against NUC and University Senate criteria.</li>
                <li>You may be contacted by the Departmental Admissions Committee if additional documentation is required.</li>
                <li>Once the Admissions Board screening is concluded, successful candidates will be notified to access their official <strong>Letter of Provisional Admission</strong> and proceed with acceptance fee confirmation.</li>
              </ul>
            </div>

            {/* Action Pathways */}
            <div className="pt-2 flex flex-wrap items-center justify-center gap-3 max-w-xl mx-auto print:hidden">
              <button
                type="button"
                onClick={() => setActiveView('screening')}
                className="py-3 px-5 bg-[#0B2644] hover:bg-[#06182B] text-white font-black text-xs rounded-xl shadow-md transition-all flex items-center space-x-2 cursor-pointer font-sc"
              >
                <Search className="w-4 h-4 text-[#B87A28]" />
                <span>Track Application in Screening Portal</span>
              </button>

              <button
                type="button"
                onClick={() => window.print()}
                className="py-3 px-5 bg-white hover:bg-slate-50 text-slate-800 font-bold text-xs rounded-xl border-2 border-slate-300 shadow-sm transition-all flex items-center space-x-2 cursor-pointer font-sc"
              >
                <Printer className="w-4 h-4 text-[#7A470E]" />
                <span>Print Application Slip</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveView('home')}
                className="py-3 px-4 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors font-sc"
              >
                Return to Portal Home
              </button>
            </div>

          </div>
        )}

      </div>

      {/* Paystack Modal Component */}
      <PaystackModal
        isOpen={showPaystackModal}
        onClose={() => setShowPaystackModal(false)}
        onSuccess={handlePaymentSuccess}
        amount={INSTITUTION_INFO.applicationFee}
        purpose="AKSU Business School Application Fee"
        applicantEmail={formData.email}
        applicantName={candidateDisplayName}
      />

    </div>
  );
};
